import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Download, Trash2, AlertTriangle, RotateCcw } from "lucide-react";
import { useForceDark } from "@/hooks/useForceDark";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PushSettingsCard } from "@/components/app/PushSettingsCard";
import { AppearanceCard } from "@/components/app/AppearanceCard";

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 3600 * 1000)));
}

export default function ChangePassword() {
  useForceDark();
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [pending, setPending] = useState(null); // deletion request
  const [loadingPending, setLoadingPending] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [undoBusy, setUndoBusy] = useState(false);

  const forced = user?.must_change_password;

  const loadPending = async () => {
    try {
      const { data } = await api.get("/users/me/deletion-request");
      setPending(data?.pending || null);
    } catch { /* ignore */ }
    finally { setLoadingPending(false); }
  };
  useEffect(() => { loadPending(); }, []); // eslint-disable-line

  // Support the ?cancel_deletion=1 shortcut so the email undo link can land here.
  useEffect(() => {
    if (params.get("cancel_deletion") === "1" && pending) {
      cancelDeletion();
    }
    // eslint-disable-next-line
  }, [pending, params]);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("Password must be at least 6 characters");
    if (next !== confirm) return toast.error("New passwords don't match");
    if (next === current) return toast.error("New password must be different");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      const { data } = await api.get("/auth/me");
      setUser(data);
      nav("/app/pitches");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update password");
    } finally { setBusy(false); }
  };

  const requestExport = async () => {
    setExportBusy(true);
    try {
      await api.post("/users/me/data-export");
      toast.success("Export queued", {
        description: `We'll email a download link to ${user?.email} within a couple of minutes.`,
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not queue your export");
    } finally { setExportBusy(false); }
  };

  const submitDeletion = async () => {
    setDeleteBusy(true);
    try {
      const { data } = await api.post("/users/me/deletion-request", { reason });
      setPending(data);
      setConfirmOpen(false);
      setReason("");
      toast.success("Deletion scheduled", {
        description: "We've emailed you a confirmation with a link to undo. Admins have also been notified.",
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not raise your deletion request");
    } finally { setDeleteBusy(false); }
  };

  const cancelDeletion = async () => {
    setUndoBusy(true);
    try {
      await api.post("/users/me/deletion-request/cancel");
      setPending(null);
      toast.success("Deletion cancelled");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not cancel the deletion");
    } finally { setUndoBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white grid place-items-start px-6 py-10 grain">
      <div className="w-full max-w-md mx-auto relative z-10 space-y-6">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Elsenham YFC" className="w-11 h-11 rounded-full object-contain" />
          <div className="font-display font-black text-lg leading-none tracking-tight">ELSENHAM YFC</div>
        </div>

        <div className="card-tactical rounded-md p-4 text-xs opacity-80" data-testid="account-details-note">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B] mb-1">Your details</div>
          Signed in as <strong>{user?.name}</strong> · {user?.email}{user?.phone ? ` · ${user.phone}` : ""}{user?.fan_number ? ` · FAN ${user.fan_number}` : ""}.
          Need to change your name, email, phone or FAN number? Please <Link to="/app/committee" className="text-[#83B68B] underline">contact a club admin</Link> — details are updated by the committee to keep FA records in step.
        </div>

        <div className="card-tactical rounded-md p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded bg-[#83B68B]/15 border border-[#83B68B]/30 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-[#83B68B]" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">{forced ? "Action required" : "Account"}</div>
              <div className="font-display font-black text-2xl leading-tight mt-1">Change password</div>
            </div>
          </div>

          {forced && (
            <div className="mb-4 p-3 bg-[#83B68B]/10 border border-[#83B68B]/30 rounded text-sm">
              This account was created for you by the club admin using a temporary password. Please set your own password to continue.
            </div>
          )}

          <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
            <div>
              <Label className="text-xs tracking-widest uppercase text-white/60">{forced ? "Temporary password" : "Current password"}</Label>
              <Input type="password" required value={current} onChange={e=>setCurrent(e.target.value)} data-testid="cp-current" className="mt-2 bg-[#0A0F1C] border-white/10" autoFocus />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-white/60">New password</Label>
              <Input type="password" required minLength={6} value={next} onChange={e=>setNext(e.target.value)} data-testid="cp-new" className="mt-2 bg-[#0A0F1C] border-white/10" />
              <div className="text-[11px] opacity-50 mt-1">At least 6 characters.</div>
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-white/60">Confirm new password</Label>
              <Input type="password" required minLength={6} value={confirm} onChange={e=>setConfirm(e.target.value)} data-testid="cp-confirm" className="mt-2 bg-[#0A0F1C] border-white/10" />
            </div>
            <button type="submit" disabled={busy} data-testid="cp-submit" className="btn-volt w-full py-3 rounded-md disabled:opacity-50">
              {busy ? "Updating…" : "Set new password"}
            </button>
            {!forced && (
              <button type="button" onClick={()=>nav(-1)} className="w-full text-sm text-white/60 hover:text-white">Cancel</button>
            )}
            {forced && (
              <button type="button" onClick={async ()=>{ await logout(); nav("/login"); }} className="w-full text-sm text-white/50 hover:text-white/80">
                Sign out instead
              </button>
            )}
          </form>
        </div>

        {!forced && <AppearanceCard />}
        {!forced && <PushSettingsCard />}

        {!forced && (
          <div className="card-tactical rounded-md p-6" data-testid="gdpr-panel">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded bg-[#83B68B]/15 border border-[#83B68B]/30 flex items-center justify-center">
                <Download className="w-5 h-5 text-[#83B68B]" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Your data · GDPR</div>
                <div className="font-display font-black text-xl leading-tight mt-1">Download my data</div>
              </div>
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              Get a ZIP of everything the club holds against your account —
              profile, bookings, teams you manage, kit orders, expense
              claims, emails we've sent you and your audit trail. We'll
              email a single-use download link that lasts 24 hours.
            </p>
            <button
              type="button"
              onClick={requestExport}
              disabled={exportBusy}
              data-testid="gdpr-export-btn"
              className="btn-volt w-full mt-5 py-3 rounded-md disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              {exportBusy ? "Queuing…" : "Email me my data"}
            </button>
            <div className="text-[11px] opacity-50 mt-3">
              We'll send it to <strong className="text-white/70">{user?.email}</strong>. If you don't see it within a couple of minutes, check your junk folder.
            </div>
          </div>
        )}

        {!forced && !loadingPending && (
          <div className="card-tactical rounded-md p-6 border-red-500/30" data-testid="deletion-panel">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded bg-red-500/15 border border-red-500/30 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-red-400">Danger zone · GDPR</div>
                <div className="font-display font-black text-xl leading-tight mt-1">Delete my account</div>
              </div>
            </div>

            {pending ? (
              <div data-testid="deletion-pending-banner">
                <div className="p-4 rounded border border-red-500/40 bg-red-500/10">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <div className="font-semibold text-red-300">Deletion scheduled</div>
                      <div className="text-white/70 mt-1">
                        Your account will be anonymised on
                        <strong className="text-white/90"> {new Date(pending.scheduled_at).toLocaleDateString("en-GB", { day:"numeric", month:"long", year:"numeric" })} </strong>
                        ({daysUntil(pending.scheduled_at)} day{daysUntil(pending.scheduled_at) === 1 ? "" : "s"} left).
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={cancelDeletion}
                  disabled={undoBusy}
                  data-testid="deletion-undo-btn"
                  className="btn-volt w-full mt-4 py-3 rounded-md disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  {undoBusy ? "Cancelling…" : "Cancel deletion — keep my account"}
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm text-white/70 leading-relaxed">
                  You can ask us to permanently anonymise your personal
                  data (name, email, phone). Your past bookings will
                  remain in the club records but will show as
                  "Deleted User". This is scheduled for
                  <strong className="text-white/90"> 14 days from now </strong>
                  so you have time to change your mind — an undo link
                  goes to your email and admins are also notified.
                </p>
                <button
                  type="button"
                  onClick={() => setConfirmOpen(true)}
                  data-testid="deletion-open-btn"
                  className="w-full mt-5 py-3 rounded-md border border-red-500/60 text-red-300 hover:bg-red-500/10 inline-flex items-center justify-center gap-2 font-display font-bold tracking-widest uppercase text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete my account
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-[#0A0F1C] border-red-500/30 text-white max-w-md" data-testid="deletion-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black tracking-tight text-2xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Delete your account?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-white/70 leading-relaxed">
              We'll schedule your account for anonymisation on
              <strong className="text-white/90"> {new Date(Date.now() + 14*24*3600*1000).toLocaleDateString("en-GB", { day:"numeric", month:"long", year:"numeric" })}</strong>.
              You'll get an email with an undo link, and you can also cancel from this page any time in the next 14 days.
            </p>
            <div>
              <Label className="text-xs tracking-widest uppercase text-white/60">Reason (optional)</Label>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                data-testid="deletion-reason-input"
                maxLength={500}
                rows={3}
                className="w-full mt-2 bg-[#111827] border border-white/10 focus:border-red-500 rounded p-2 text-sm resize-none"
                placeholder="Helps admins improve the club if you want to share…"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="flex-1 py-3 rounded-md border border-white/20 hover:border-white/40 text-sm font-display tracking-widest uppercase"
              >
                Never mind
              </button>
              <button
                type="button"
                onClick={submitDeletion}
                disabled={deleteBusy}
                data-testid="deletion-submit-btn"
                className="flex-1 py-3 rounded-md bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-display font-bold tracking-widest uppercase inline-flex items-center justify-center gap-2"
              >
                {deleteBusy ? "Scheduling…" : (<><Trash2 className="w-4 h-4" /> Yes, delete</>)}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
